// src/admin/AdminProductForm.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  /* =========================================================
     ÉTATS
  ========================================================= */
  const [form, setForm] = useState({
    nom: '',
    nom_en: '',
    description: '',
    description_en: '',
    prix: '',
    stock: 0,
    meta_title: '',
    meta_description: '',
    is_request_only: false,       // ⭐
  });

  const [images, setImages] = useState({
    image: null,
    image2: null,
    image3: null,
    og_image: null,
  });

  const [previews, setPreviews] = useState({
    image: '',
    image2: '',
    image3: '',
    og_image: '',
  });

  const [categories, setCategories] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /* =========================================================
     CHARGER LES CATÉGORIES
  ========================================================= */
  useEffect(() => {
    axios.get('https://madadline.com/api/categories')
      .then(({ data }) => {
        let list = [];
        if (Array.isArray(data)) {
          list = data.map(item => {
            if (typeof item === 'string') return { id: item, name: item };
            return { id: item.id ?? item.name, name: item.name ?? '' };
          }).filter(c => c.name);
        }
        setCategories(list);
      })
      .catch((err) => {
        console.error('Erreur catégories:', err);
        setCategories([]);
      });
  }, []);

  /* =========================================================
     CHARGER LE PRODUIT (édition)
  ========================================================= */
  useEffect(() => {
    if (!isEditing) return;

    axios.get(`https://madadline.com/api/products/${id}`)
      .then(({ data }) => {
        setForm({
          nom: data.nom || '',
          nom_en: data.nom_en || '',
          description: data.description || '',
          description_en: data.description_en || '',
          prix: data.prix || '',
          stock: data.stock ?? 0,
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          is_request_only: Boolean(data.is_request_only),   // ⭐
        });

        setPreviews({
          image: data.image ? `https://madadline.com/${data.image}` : '',
          image2: data.image2 ? `https://madadline.com/${data.image2}` : '',
          image3: data.image3 ? `https://madadline.com/${data.image3}` : '',
          og_image: data.og_image ? `https://madadline.com/${data.og_image}` : '',
        });

        if (data.categories) {
          setCategoryIds(data.categories.map(c => c.id));
        }
      })
      .catch(() => setError('Produit introuvable.'));
  }, [id, isEditing]);

  /* =========================================================
     HANDLERS
  ========================================================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: checked }));
  };

  const handleImageChange = (e) => {
    const { name, files } = e.target;
    if (!files?.[0]) return;

    const file = files[0];
    setImages(prev => ({ ...prev, [name]: file }));

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => ({ ...prev, [name]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const toggleCategory = (catId) => {
    setCategoryIds(prev =>
      prev.includes(catId) ? prev.filter(x => x !== catId) : [...prev, catId]
    );
  };

  /* =========================================================
     SOUMISSION
  ========================================================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (categoryIds.length === 0) {
      setError('Veuillez sélectionner au moins une catégorie.');
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const fd = new FormData();

      Object.entries(form).forEach(([k, v]) => {
        if (v === null || v === undefined) return;

        // ⭐ booléens → '1' / '0' (FormData ne supporte que string/blob)
        if (typeof v === 'boolean') {
          fd.append(k, v ? '1' : '0');
        } else {
          fd.append(k, v);
        }
      });

      categoryIds.forEach(catId => fd.append('category_ids[]', catId));

      ['image', 'image2', 'image3', 'og_image'].forEach(k => {
        if (images[k]) fd.append(k, images[k]);
      });

      if (isEditing) {
        fd.append('_method', 'PUT');
        await axios.post(`https://madadline.com/api/products/${id}`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await axios.post('https://madadline.com/api/products', fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      navigate('/admin/produits');
    } catch (err) {
      console.error(err);
      const errs = err.response?.data?.errors;
      const first = errs ? Object.values(errs)[0]?.[0] : null;
      setError(first || err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-product-form">

      <header className="admin-page-head">
        <div>
          <h1>{isEditing ? 'Modifier le produit' : 'Nouveau produit'}</h1>
          <p>{isEditing ? 'Modifiez les informations du produit' : 'Ajoutez une nouvelle pièce au catalogue'}</p>
        </div>
        <button onClick={() => navigate('/admin/produits')} className="admin-btn">
          ← Retour
        </button>
      </header>

      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="admin-form__grid">

          <div className="admin-form__group">
            <label>Nom (FR) *</label>
            <input name="nom" value={form.nom} onChange={handleChange} required />
          </div>

          <div className="admin-form__group">
            <label>Nom (EN)</label>
            <input name="nom_en" value={form.nom_en} onChange={handleChange} />
          </div>

          <div className="admin-form__group">
            <label>Prix (DH) *</label>
            <input type="number" name="prix" value={form.prix} onChange={handleChange} min="0" required />
          </div>

          {/* ⭐ Toggle "sur commande uniquement" */}
          <div className="admin-form__group admin-form__group--full">
            <label className="admin-toggle">
              <input
                type="checkbox"
                name="is_request_only"
                checked={form.is_request_only}
                onChange={handleCheckbox}
              />
              <span className="admin-toggle__slider" />
              <span className="admin-toggle__label">
                <strong>Produit « sur commande uniquement »</strong>
                <small>
                  Coché → le client ne peut commander que via « Commande sur commande » (WhatsApp).
                  Décoché → le checkout classique reste disponible.
                </small>
              </span>
            </label>
          </div>

          {/* ⭐ Catégories multiples */}
          <div className="admin-form__group admin-form__group--full">
            <label>Catégories * (sélection multiple)</label>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 10,
              padding: 12, border: '1px solid #EAE6DA',
              borderRadius: 8, background: '#F8F6F0',
            }}>
              {categories.length === 0 ? (
                <span style={{ color: '#888', fontSize: 13 }}>Chargement…</span>
              ) : (
                categories.map(c => (
                  <label
                    key={c.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', background: 'white',
                      borderRadius: 6, cursor: 'pointer',
                      border: categoryIds.includes(c.id)
                        ? '2px solid #C21E56'
                        : '1px solid #EAE6DA',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={categoryIds.includes(c.id)}
                      onChange={() => toggleCategory(c.id)}
                    />
                    <span>{c.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="admin-form__group admin-form__group--full">
            <label>Description (FR)</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows="4" />
          </div>

          <div className="admin-form__group admin-form__group--full">
            <label>Description (EN)</label>
            <textarea name="description_en" value={form.description_en} onChange={handleChange} rows="4" />
          </div>

          {/* ⭐ Message info stock */}
          <div className="admin-form__group admin-form__group--full">
            <div className="admin-alert admin-alert--warning" style={{ fontSize: 13 }}>
              💡 <strong>Info :</strong> Le stock se gère par variante (taille + couleur).
              {isEditing && (
                <span>
                  {' '}
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/produits/${id}/variantes`)}
                    style={{
                      background: 'none', border: 'none', color: '#C21E56',
                      textDecoration: 'underline', cursor: 'pointer',
                      padding: 0, font: 'inherit',
                    }}
                  >
                    Gérer les variantes →
                  </button>
                </span>
              )}
            </div>
          </div>

          {/* ⭐ Images produit */}
          {['image', 'image2', 'image3'].map((key, i) => (
            <div key={key} className="admin-form__group">
              <label>Image {i + 1} {i === 0 && '*'}</label>
              <input type="file" name={key} accept="image/*" onChange={handleImageChange} />
              {previews[key] && (
                <div className="admin-form__preview">
                  <img src={previews[key]} alt={`Preview ${i + 1}`} />
                </div>
              )}
            </div>
          ))}

          {/* ⭐ SEO — Meta Title */}
          <div className="admin-form__group admin-form__group--full">
            <label>Meta Title (SEO)</label>
            <input
              type="text"
              name="meta_title"
              value={form.meta_title}
              onChange={handleChange}
              placeholder="Titre affiché sur Google et les réseaux sociaux"
              maxLength={60}
            />
            <small style={{ fontSize: 11, color: '#888' }}>
              {form.meta_title.length}/60 caractères — laissez vide pour utiliser le nom du produit
            </small>
          </div>

          {/* ⭐ SEO — Meta Description */}
          <div className="admin-form__group admin-form__group--full">
            <label>Meta Description (SEO)</label>
            <textarea
              name="meta_description"
              value={form.meta_description}
              onChange={handleChange}
              placeholder="Description affichée sous le titre sur Google et les réseaux sociaux"
              rows={3}
              maxLength={160}
            />
            <small style={{ fontSize: 11, color: '#888' }}>
              {form.meta_description.length}/160 caractères
            </small>
          </div>

          {/* ⭐ SEO — Image OG */}
          <div className="admin-form__group admin-form__group--full">
            <label>Image de partage (Open Graph)</label>
            <input
              type="file"
              name="og_image"
              accept="image/*"
              onChange={handleImageChange}
            />
            {previews.og_image && (
              <div className="admin-form__preview">
                <img src={previews.og_image} alt="Aperçu OG" />
              </div>
            )}
            <small style={{ fontSize: 11, color: '#888' }}>
              Recommandé : 1200×630 px. Laissez vide pour utiliser l'image principale.
            </small>
          </div>
        </div>

        <div className="admin-form__actions">
          <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
            {saving ? 'Enregistrement…' : (isEditing ? 'Mettre à jour' : 'Créer le produit')}
          </button>
          <button
            type="button"
            className="admin-btn"
            onClick={() => navigate('/admin/produits')}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminProductForm;